import * as React from 'react';

/**
 * Whether the referenced box's content overflows it, so a scroll surface gets
 * a tab stop only when there is something to scroll. A scrollable box a
 * keyboard cannot reach fails WCAG 2.1 AA (axe `scrollable-region-focusable`)
 * and a focusable one with nothing to scroll is a dead stop, so the answer has
 * to be measured rather than assumed either way.
 *
 * `jsdom` has no `ResizeObserver` and lays nothing out, so under test this is
 * always `false` -- the honest answer for a box measured at 0x0, and it keeps
 * a table's tab order under test what it is in a viewport the table fits.
 */
export function useOverflows(): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = React.useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = React.useState(false);

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      setOverflows(node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // The content box as well: content that grows a row inside a container
    // whose own size is fixed never resizes the container.
    if (node.firstElementChild !== null) observer.observe(node.firstElementChild);
    return () => observer.disconnect();
  }, []);

  return [ref, overflows];
}
